import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (mirrors study_companion/mood_tracker's R8
// graduation). askBrain now sends the message + this app's declared action catalog to the
// generic fleet chat brain (POST /api/apps/oriora_outgrown/chat) — ONE billed call returns ONE
// registered action + a reply; this file resolves references (category text → the latest
// matching SizeRecord) locally, because the data lives here, then executes NOTHING itself —
// executeAction (chat/actions.ts) does that, unchanged.
//
// The regex intent-router below (routeMessage) is the FREE FALLBACK (offline / signed-out /
// out of funds) — chat never goes dead. Original prototype notes:
//   • "log shoes size 13 brand Clarks"        → local store.createSizeRecord        → add_size
//   • "edit shoes size to 13" / "fix …"       → resolve latest match, local update   → edit_size
//   • "delete shoes size" / "remove …"        → resolve latest match locally        → delete_size (UI confirms)
//   • "search deposit" / "find clarks"        → local filter over sizes + inventory  → search   (free)
//   • "recap" / "summarize" / "overview"      → EXISTING sizingRecap(signals)        → recap    (billed)
//   • a real question ("what size is she in now?") → EXISTING askSizes(signals, q)   → ask      (billed)
//   • anything else                            → none + a short clarifying nudge
//
// The two billed AI steps (sizingRecap / askSizes) run HERE — in toAction(), same as they used
// to run in routeMessage — because the app's own ChatAction shape (chat/actions.ts, UNCHANGED)
// carries the narration text/answer ALREADY FILLED IN (`recap.text`, `ask.answer`); the executor
// only renders it, it never calls the AI itself. So the generic /chat call decides INTENT (recap
// vs ask vs a CRUD action), then this file makes the ONE further billed call the intent needs —
// still one user action → one billed narration call, never chained/looped.
//
// Same CHILDREN-domain guardrail applies throughout: sizingRecap/askSizes only narrate
// sizes/dates/inventory logistics, never health or growth (enforced server-side; see ../brain).
// This app has no pre-LLM refusal fence to import (unlike study_companion's academicRefusal) —
// the guardrail lives entirely in the server-side prompts for sizing-recap/ask, so there is
// nothing to run before the generic chat call either (fence-by-catalog-omission: no
// health/growth-shaped action exists in CHAT_ACTIONS to begin with).

import { store, todayLocal, type SizeRecord, type SizeRecordPatch, type InventoryItem } from "../store";
import { sizingRecap, askSizes, BrainError, brainCall } from "../brain";
import { childHealthRefusal } from "../child-health-fence";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: import("./actions").ResultRow[];
}
export interface BrainResponse {
  reply: string;         // CHILD-SAFETY: kept "" — never surface the model's freeform reply.
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth on top of the server-side sizing fence
// + reply-discard). Child-adjacent (clothing sizes) — keep it to sizes/logistics, never growth/health.
const SCOPE =
  "This app is a private children's CLOTHING-SIZE log: track what sizes fit each child and when, edit/search/" +
  "delete sizes, and give an OBSERVATIONAL recap/answer about the child's OWN logged sizes and inventory logistics " +
  "(what fits now, what's outgrown, hand-me-down timing). Hard limits: never comment on the child's GROWTH, " +
  "development, or health, never say whether a child is normal/small/big/on-track, never give parenting or medical " +
  "advice — those belong with a pediatrician. Back up to their own cloud.";
export interface ChatContext {
  childId: string;
  childName: string;
}

const HELP =
  "I can log a size, fix a mistake, remove one, search sizes and bought-ahead items, recap, or answer a question — just tell me. Try:\n" +
  '• "log shoes size 13 brand Clarks"\n' +
  '• "edit shoes size to 13"\n' +
  '• "search clarks"\n' +
  '• "recap"\n' +
  '• "what size is she in now?"\n' +
  '• "delete shoes size"';

const numOrNull = (label: string): number | null => (/^\d+(\.\d+)?$/.test(label) ? Number(label) : null);

// Question-shaped → route to the grounded (billed) /ask endpoint.
const QUESTION_RE = /(\?|\b(what|which|when|where|how|does|do|is|are|can|could|will|who|why)\b)/i;
const RECAP_RE = /\b(recap|summari[sz]e|sum up|overview)\b/i;

function findLatestByCategory(records: SizeRecord[], term: string): { matches: SizeRecord[]; distinctCats: string[] } {
  const t = term.toLowerCase();
  let matches = records.filter((r) => r.category.toLowerCase().includes(t));
  if (matches.length === 0) {
    // Fleet FUZZY standard (fuzzy.ts): forgiving near-match on the category ("shooes" → "shoes").
    const cats = [...new Set(records.map((r) => r.category))].map((c) => ({ name: c }));
    const near = new Set(findNamedMatches(cats, term).map((c) => c.name.toLowerCase()));
    if (near.size) matches = records.filter((r) => near.has(r.category.toLowerCase()));
  }
  const distinctCats = [...new Set(matches.map((r) => r.category))];
  return { matches, distinctCats };
}

// Inventory (bought-ahead garment) name→id resolution for the chat photo attach. The item's "name"
// is its description (falling back to category). Substring first, then the fleet fuzzy near-match.
const inventoryName = (i: InventoryItem): string => [i.description, i.category, i.size_label].filter(Boolean).join(" ").trim();
export async function findInventoryByName(childId: string, term: string): Promise<{ matches: InventoryItem[]; notFound: string }> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  const all = await store.listInventory(childId);
  if (!q) return { matches: [], notFound: notFoundMessage(all.map((i) => ({ name: inventoryName(i) })), term) };
  const subs = all.filter((i) => inventoryName(i).toLowerCase().includes(q));
  const matches = subs.length
    ? subs
    : findNamedMatches(all.map((i) => ({ ...i, name: inventoryName(i) })), term).map(({ name: _n, ...i }) => i as InventoryItem);
  return { matches, notFound: notFoundMessage(all.map((i) => ({ name: inventoryName(i) })), term) };
}

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.outgrown.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_size", description: "Log a new size for the active child.", params: { category: "the item category, e.g. shoes, pants, jacket", sizeLabel: "the size as the user said it, e.g. 13, 4T, 10Y", brand: "optional brand name", note: "optional short note" } },
  { name: "edit_size", description: "Fix/correct the MOST RECENT logged size in one category for the active child.", params: { category: "which category to fix — as the user wrote it", sizeLabel: "the corrected size label", brand: "optional corrected brand" } },
  { name: "delete_size", description: "Remove the most recent logged size in one category for the active child (the app asks the user to confirm first).", params: { category: "which category to remove — as the user wrote it" } },
  { name: "search", description: "Search the child's sizes and bought-ahead inventory for a free-text term.", params: { query: "the search text" } },
  { name: "recap", description: "Narrate a summary of the child's sizing trends — current sizes, changes, bought-ahead status. Sizes/logistics only, never health or growth.", params: {} },
  { name: "ask", description: "Answer a specific question about the child's own logged sizes. Sizes/logistics only — never health, growth percentiles, or development.", params: { question: "the user's question, as asked" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole collection — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

async function buildContext(ctx: ChatContext): Promise<string> {
  const parts = [`Active child: ${ctx.childName}.`];
  try {
    const records = await store.listSizeRecords(ctx.childId);
    const cats = [...new Set(records.map((r) => r.category))];
    if (cats.length) parts.push(`Categories already logged for ${ctx.childName}: ${cats.slice(0, 20).join(", ")}.`);
  } catch { /* context is optional */ }
  return parts.join(" ");
}

async function serverChat(history: ChatTurn[], text: string, ctx: ChatContext, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = (await buildContext(ctx)) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  // CHILD-SAFETY: never surface the model's freeform reply — reply stays "".
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, ctx);
    actions.push(one.action);
  }
  return { reply: "", actions };
}

export async function toAction(r: ServerChat, ctx: ChatContext): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }

  // ── DATA PORTABILITY — handled EARLY too, same reasoning as the library block above:
  // backing up/restoring/importing the WHOLE collection belongs to the customer, not to a
  // currently-active child, and restore's primary real use case (reinstalling the app) is
  // exactly the moment no child exists yet. Previously these sat inside the switch below the
  // `!ctx.childId` guard, so "restore my data" on a fresh install was silently refused with
  // "Add a child first…" — the identical shape of bug the comment above already names.
  if (r.action === "backup_save") return { reply: say, action: { kind: "backup_save" } };
  if (r.action === "backup_status") return { reply: say, action: { kind: "backup_status" } };
  if (r.action === "backup_restore") {
    const prov = asStr(p.provider);
    return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
  }
  if (r.action === "import_file") return { reply: say, action: { kind: "import_file" } };

  // Everything BELOW is about a specific child. The chat now opens before one exists (so the
  // library and data-portability actions above are reachable on a fresh install), so ask for
  // the child here rather than gating the whole panel — the guard must never sit above them.
  if (!ctx.childId) return none("Add a child first, then I can log and check sizes for them.");

  switch (r.action) {
    case "add_size": {
      const category = asStr(p.category);
      const sizeLabel = asStr(p.sizeLabel ?? p.size_label ?? p.size);
      if (!category || !sizeLabel) return none(say || 'Give me a category and a size, e.g. "log shoes size 13".');
      const brand = asStr(p.brand) ?? "";
      const note = asStr(p.note) ?? "";
      return { reply: say, action: { kind: "add_size", category, sizeLabel, sizeValue: numOrNull(sizeLabel), brand, note } };
    }

    case "edit_size": {
      const term = asStr(p.category);
      const sizeLabel = asStr(p.sizeLabel ?? p.size_label ?? p.size);
      if (!term) return none(say || "Which size should I fix?");
      if (!sizeLabel) return none(say || "What should the new size be?");
      const records = await store.listSizeRecords(ctx.childId);
      const { matches, distinctCats } = findLatestByCategory(records, term);
      if (matches.length === 0) return none(`${ctx.childName} doesn't have a logged "${term}" size yet.`);
      if (distinctCats.length > 1) {
        const list = distinctCats.slice(0, 6).map((c) => `• ${c}`).join("\n");
        return none(`"${term}" matches more than one category — be more specific:\n${list}`);
      }
      const rec = matches[0];
      const patch: SizeRecordPatch = { size_label: sizeLabel, size_value: numOrNull(sizeLabel) };
      const brand = asStr(p.brand);
      if (brand !== undefined) patch.brand = brand;
      return { reply: say, action: { kind: "edit_size", id: rec.id, label: `${rec.category} size`, patch } };
    }

    case "delete_size": {
      const term = asStr(p.category);
      if (!term) return none(say || "Which size should I remove?");
      const records = await store.listSizeRecords(ctx.childId);
      const { matches, distinctCats } = findLatestByCategory(records, term);
      if (matches.length === 0) return none(`I couldn't find a "${term}" size for ${ctx.childName}.`);
      if (distinctCats.length > 1) {
        const list = distinctCats.slice(0, 6).map((c) => `• ${c}`).join("\n");
        return none(`"${term}" matches more than one category — be more specific:\n${list}`);
      }
      const rec = matches[0];
      return { reply: say, action: { kind: "delete_size", id: rec.id, label: `${rec.category} size ${rec.size_label}` } };
    }

    case "search": {
      const query = asStr(p.query);
      if (!query) return none(say || "What should I search for?");
      return { reply: "", action: { kind: "search", query } };
    }

    case "recap": {
      try {
        const signals = await store.computeSignals(ctx.childId, todayLocal());
        const rr = await sizingRecap(signals);
        return { reply: "", action: { kind: "recap", text: rr.recap } };
      } catch (e) {
        if (e instanceof BrainError) return none(e.message);
        throw e;
      }
    }

    case "ask": {
      const question = asStr(p.question);
      if (!question) return none(say || "What would you like to ask about the sizes?");
      try {
        const signals = await store.computeSignals(ctx.childId, todayLocal());
        const rr = await askSizes(signals, question);
        return { reply: "", action: { kind: "ask", question, answer: rr.answer } };
      } catch (e) {
        if (e instanceof BrainError) return none(e.message);
        throw e;
      }
    }

    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function askBrain(history: ChatTurn[], ctx: ChatContext, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  // CHILD-HEALTH FENCE — pre-LLM, free, on BOTH paths. A growth/development/health-normality
  // ask is deflected to a professional before any billed call (sizes-as-logistics asks pass).
  { const m = childHealthRefusal(text); if (m) return { reply: "", actions: [{ kind: "none", message: m }] }; }
  try {
    return await serverChat(history, text, ctx, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      const r0 = none(e.message);
      return { reply: r0.reply, actions: [r0.action] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text, ctx);
    return { reply: r.reply, actions: [r.action] };
  }
}

export async function routeMessage(text: string, ctx: ChatContext): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  { const m = childHealthRefusal(t); if (m) return none(m); }

  // 1) ADD — "log/add/save <category> size <label> [brand <brand>] [note <text>]".
  const addM = t.match(/^(?:log|add|save)\s+(?:a\s+)?(.+?)\s+size\s+(.+)$/i);
  if (addM) {
    const category = addM[1].trim();
    const rest = addM[2].trim();
    const brandM = rest.match(/\bbrand\s+(.+?)(?=\s+note\b|$)/i);
    const noteM = rest.match(/\bnote\s+(.+)$/i);
    const brand = brandM ? brandM[1].trim() : "";
    const note = noteM ? noteM[1].trim() : "";
    const sizeLabel = rest.replace(/\bbrand\s+.+$/i, "").replace(/\bnote\s+.+$/i, "").trim();
    if (!category || !sizeLabel) {
      return { reply: "", action: { kind: "none", message: 'Give me a category and a size, e.g. "log shoes size 13".' } };
    }
    return { reply: "", action: { kind: "add_size", category, sizeLabel, sizeValue: numOrNull(sizeLabel), brand, note } };
  }

  // 2) EDIT — "edit/fix/correct/update <category> size [to] <label> [brand <brand>]". Resolves the
  // MOST RECENT record in that category (records arrive newest-first from SQLite).
  const editM = t.match(/^(?:edit|fix|correct|update)\s+(?:the\s+)?(.+?)\s+size(?:\s+to)?\s+(.+)$/i);
  if (editM) {
    const term = editM[1].trim();
    const rest = editM[2].trim();
    const brandM = rest.match(/\bbrand\s+(.+)$/i);
    const brand = brandM ? brandM[1].trim() : undefined;
    const sizeLabel = rest.replace(/\bbrand\s+.+$/i, "").trim();
    if (!term) return { reply: "", action: { kind: "none", message: "Which size should I fix?" } };
    if (!sizeLabel) return { reply: "", action: { kind: "none", message: "What should the new size be?" } };
    const records = await store.listSizeRecords(ctx.childId);
    const { matches, distinctCats } = findLatestByCategory(records, term);
    if (matches.length === 0) {
      return { reply: "", action: { kind: "none", message: `${ctx.childName} doesn't have a logged “${term}” size yet.` } };
    }
    if (distinctCats.length > 1) {
      const list = distinctCats.slice(0, 6).map((c) => `• ${c}`).join("\n");
      return { reply: "", action: { kind: "none", message: `“${term}” matches more than one category — be more specific:\n${list}` } };
    }
    const rec = matches[0];
    const patch: SizeRecordPatch = { size_label: sizeLabel, size_value: numOrNull(sizeLabel) };
    if (brand !== undefined) patch.brand = brand;
    return { reply: "", action: { kind: "edit_size", id: rec.id, label: `${rec.category} size`, patch } };
  }

  // 3) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|forget)\s+(?:the\s+)?(.+?)\s+size\s*$/i);
  if (delM) {
    const term = delM[1].trim().replace(/[.?!]+$/, "");
    if (!term) return { reply: "", action: { kind: "none", message: "Which size should I remove?" } };
    const records = await store.listSizeRecords(ctx.childId);
    const { matches, distinctCats } = findLatestByCategory(records, term);
    if (matches.length === 0) {
      return { reply: "", action: { kind: "none", message: `I couldn't find a “${term}” size for ${ctx.childName}.` } };
    }
    if (distinctCats.length > 1) {
      const list = distinctCats.slice(0, 6).map((c) => `• ${c}`).join("\n");
      return { reply: "", action: { kind: "none", message: `“${term}” matches more than one category — be more specific:\n${list}` } };
    }
    const rec = matches[0];
    return { reply: "", action: { kind: "delete_size", id: rec.id, label: `${rec.category} size ${rec.size_label}` } };
  }

  // 4) RECAP — the ONE billed, existing sizingRecap() call, reused as-is.
  if (RECAP_RE.test(t)) {
    try {
      const signals = await store.computeSignals(ctx.childId, todayLocal());
      const r = await sizingRecap(signals);
      return { reply: "", action: { kind: "recap", text: r.recap } };
    } catch (e) {
      if (e instanceof BrainError) return { reply: "", action: { kind: "none", message: e.message } };
      throw e;
    }
  }

  // 5) SEARCH — explicit search verb → local filter over sizes + inventory (free, no bill). A
  // question-shaped phrase after the verb ("show me what my lease says…") falls through to ASK.
  const searchM = t.match(/^(?:search|find|look\s+up|look\s+for|show\s+me)\s+(?:for\s+)?(.+)$/i);
  if (searchM && !QUESTION_RE.test(searchM[1])) {
    const query = searchM[1].trim().replace(/[.?!]+$/, "");
    if (query) return { reply: "", action: { kind: "search", query } };
  }

  // 6) ASK — a real question → the ONE billed, existing askSizes() call, reused as-is.
  if (QUESTION_RE.test(t)) {
    try {
      const signals = await store.computeSignals(ctx.childId, todayLocal());
      const r = await askSizes(signals, t);
      return { reply: "", action: { kind: "ask", question: t, answer: r.answer } };
    } catch (e) {
      if (e instanceof BrainError) return { reply: "", action: { kind: "none", message: e.message } };
      throw e;
    }
  }

  // 7) Unclear → a short clarifying nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

export { HELP };

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
